import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  DuplicateProjectRequestBody,
  DuplicateProjectResponseBody,
} from "@imagine/ai-service/shared-types/route-types";
import { toast } from "sonner";
import { imagineRoutingStore } from "@/web-components";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { HttpError } from "@/lib/http-error";

export function useDuplicateProject({
  onSuccess,
  onError,
}: { onSuccess?: () => void; onError?: (error: Error) => void } = {}) {
  const queryClient = useQueryClient();
  const navigate = imagineRoutingStore.use.navigate();

  return useMutation<
    DuplicateProjectResponseBody,
    Error,
    DuplicateProjectRequestBody
  >({
    mutationFn: async (params) => {
      const response = await aiServiceFetch(
        `/api/projects/${params.projectId}/duplicate`,
        {
          method: "POST",
          body: JSON.stringify(params),
        },
      );

      if (!response.ok) {
        throw await HttpError.fromResponse(response);
      }

      return response.json();
    },
    onError: (error) => {
      onError?.(error);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["my-projects"] });
      toast.success("Project duplicated successfully");
      navigate({
        id: "project",
        props: { projectId: data.newProjectId, region: "fra" },
      });
      onSuccess?.();
    },
  });
}
